import { AddCircleOutline, CameraAlt, Clear } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

import { SelectedSerial } from './MobilePartActionsDialog.props';
import {
  PartTypeName,
  SelectedSerialChip,
  SerialInfo,
  SerialLabel,
  SerialNumber,
  SerialSection,
} from './MobilePartActionsDialog.styles';
import { PartTypeActionLiveSearchItem } from '../../../../../../@types/partType.type';
import { BarcodeScanner } from '../../../../../../components/molecules/BarcodeScanner';
import { BottomSheet } from '../../../../../../components/molecules/BottomSheet';
import { COLORS } from '../../../../../../configs/colors';

interface PartSerialSelectorProps {
  label: string;
  selectedSerial: SelectedSerial | null;
  onSelect: (item: PartTypeActionLiveSearchItem | null) => void;
  onClear: () => void;
  onSearch: (value: string) => Promise<PartTypeActionLiveSearchItem[]>;
  isSearching: boolean;
  borderColor?: string;
  onCreateExceptionally?: (serial: string) => void;
}

export const PartSerialSelector: React.FC<PartSerialSelectorProps> = ({
  label,
  selectedSerial,
  onSelect,
  onClear,
  onSearch,
  isSearching,
  borderColor,
  onCreateExceptionally,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [options, setOptions] = useState<PartTypeActionLiveSearchItem[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    },
    []
  );

  const handleInputChange = (value: string): void => {
    setSearchInput(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (value.length >= 2) {
      searchTimerRef.current = setTimeout(async () => {
        try {
          const results = await onSearch(value);
          setOptions(results);
        } catch {
          setOptions([]);
        } finally {
          setHasSearched(true);
        }
      }, 300);
    } else {
      setOptions([]);
      setHasSearched(false);
    }
  };

  const handleSelect = (item: PartTypeActionLiveSearchItem | null): void => {
    if (item) {
      onSelect(item);
      setSearchInput('');
      setOptions([]);
    }
  };

  const handleScan = async (scannedValue: string): Promise<void> => {
    setScannerOpen(false);
    setSearchInput(scannedValue);
    try {
      const results = await onSearch(scannedValue);
      setOptions(results);
      if (results.length === 1) handleSelect(results[0]);
    } catch {
      setOptions([]);
    } finally {
      setHasSearched(true);
    }
  };

  if (selectedSerial) {
    return (
      <SerialSection>
        <SerialLabel>{label}</SerialLabel>
        <SelectedSerialChip sx={borderColor ? { borderColor } : undefined}>
          <SerialInfo>
            <SerialNumber>{selectedSerial.serial}</SerialNumber>
            <PartTypeName>{selectedSerial.partTypeName}</PartTypeName>
          </SerialInfo>
          <IconButton size="small" onClick={onClear} aria-label="Clear selection">
            <Clear fontSize="small" />
          </IconButton>
        </SelectedSerialChip>
      </SerialSection>
    );
  }

  return (
    <SerialSection>
      <SerialLabel>{label}</SerialLabel>
      <Autocomplete
        freeSolo
        options={options}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : `${option.serial1} - ${option.partTypeName}`
        }
        inputValue={searchInput}
        onInputChange={(_, value) => handleInputChange(value)}
        onChange={(_, value) => {
          if (value && typeof value !== 'string') handleSelect(value);
        }}
        loading={isSearching}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Search by serial number..."
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearching && <CircularProgress size={20} />}
                  <InputAdornment position="end">
                    <Tooltip title="Scan barcode">
                      <IconButton
                        size="small"
                        onClick={() => setScannerOpen(true)}
                        aria-label="Scan barcode"
                        sx={{ minWidth: 44, minHeight: 44 }}
                      >
                        <CameraAlt fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.partId}>
            <div>
              <div style={{ fontWeight: 600 }}>{option.serial1}</div>
              <div style={{ fontSize: 12, color: COLORS.Gray }}>{option.partTypeName}</div>
            </div>
          </li>
        )}
        noOptionsText={searchInput.length < 2 ? 'Type at least 2 characters' : 'No parts found'}
      />

      {onCreateExceptionally && hasSearched && !isSearching && (
        <Box sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddCircleOutline />}
            onClick={() => onCreateExceptionally(searchInput)}
            fullWidth
            sx={{ textTransform: 'none', fontSize: 13 }}
          >
            {options.length === 0
              ? 'Part not found — create exceptionally'
              : 'Create part exceptionally'}
          </Button>
        </Box>
      )}

      <BottomSheet
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        height="large"
        title="Scan Serial"
      >
        <BarcodeScanner onDetect={handleScan} />
      </BottomSheet>
    </SerialSection>
  );
};
